import {
  AccessTime as TimeIcon,
  Close as CloseIcon,
  Event as EventIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { Box, Button, Card, CardContent, Drawer, IconButton, Stack, Typography } from "@mui/material";

import { SearchField } from "~ui/atoms/SearchField/SearchField";
import { AddEventTimesModal } from "~ui/organisms/AddEventTimesModal/AddEventTimesModal";

import { EventFormModal } from "../EventFormModal/EventFormModal";
import { useEventsSlideout } from "./useEventsSlideout";

// Centralized strings for internationalization
const tEvents = "Events";
const tNoEvents = "No Events Yet";
const tNoEventsDesc = "Events will appear here when they are created";
const tNoResults = "No events found";
const tNoResultsDesc = "Try searching with different keywords";
const tSearchPlaceholder = "Search events...";
const tViewDetails = "View Details";
const tCapacity = "Capacity:";
const tCapacityUnlimited = "Unlimited";
const tCapacityPerson = "1 person";
const tCapacityPeople = (count: number) => `${count} people`;
const tDuration = "Duration:";
const tNoUpcoming = "No upcoming meeting scheduled";

interface EventSession {
  id: string;
  name: string;
  nextMeetingTime?: {
    capacity: number;
    id: string;
    beginMeetingTime: string;
    endMeetingTime: string;
  } | null;
}

interface NewEvent {
  bubbleId: string;
  description: string;
  isAutoJoin: boolean;
  isSearchable: boolean;
  name: string;
}

interface EventsSlideoutProps {
  open: boolean;
  onClose: () => void;
  events: EventSession[];
  roomId: string;
  canManage?: boolean;
  onAddEvent?: (event: NewEvent) => void;
  bubbleSelectItems?: { id: string; value: string }[];
  defaultBubbleId?: string;
}

export const EventsSlideout = ({
  open,
  onClose,
  events,
  canManage = false,
  onAddEvent,
  bubbleSelectItems = [],
  defaultBubbleId,
}: EventsSlideoutProps) => {
  const {
    searchQuery,
    setSearchQuery,
    filteredEvents,
    creatingMeetingTimes,
    handleEventClick,
    handleCreateMeetingTimes,
    formatDateTime,
    formatDuration,
  } = useEventsSlideout(events);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      role="complementary"
      aria-label="Events"
      sx={{
        "& .MuiDrawer-paper": {
          borderRadius: "16px 0 0 16px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          width: { sm: 400, xs: "100%" },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            alignItems: "center",
            background: "linear-gradient(135deg, #4CAF50 0%, #2196F3 100%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            p: 3,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <EventIcon sx={{ fontSize: "2rem" }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {tEvents}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {filteredEvents.length === 1 ? "1 event" : `${filteredEvents.length} events`}
                {searchQuery && ` of ${events.length}`}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            aria-label="Close events"
            sx={{
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Add Event Button */}
        {canManage && onAddEvent && (
          <Box sx={{ borderBottom: "1px solid", borderColor: "divider", p: 2 }}>
            <EventFormModal
              onSubmit={onAddEvent}
              bubbleSelectItems={bubbleSelectItems}
              initialValues={{
                bubbleId: defaultBubbleId ?? "",
                description: "",
                isAutoJoin: false,
                isSearchable: false,
                name: "",
              }}
              mode="create"
            />
          </Box>
        )}

        {/* Search Field */}
        <Box sx={{ borderBottom: "1px solid", borderColor: "divider", p: 2 }}>
          <SearchField value={searchQuery} onChange={setSearchQuery} placeholder={tSearchPlaceholder} size="small" />
        </Box>

        {/* Events List */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 2,
          }}
        >
          {/* No Search Results State */}
          {searchQuery && filteredEvents.length === 0 && (
            <Box
              role="status"
              aria-label="No events found"
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "100%",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <EventIcon sx={{ color: "text.secondary", fontSize: "4rem" }} />
              <Typography variant="h6" color="text.secondary">
                {tNoResults}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tNoResultsDesc}
              </Typography>
            </Box>
          )}

          {/* Empty State */}
          {!searchQuery && events.length === 0 && (
            <Box
              role="status"
              aria-label="No events"
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "100%",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <EventIcon sx={{ color: "text.secondary", fontSize: "4rem" }} />
              <Typography variant="h6" color="text.secondary">
                {tNoEvents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tNoEventsDesc}
              </Typography>
            </Box>
          )}

          {/* Events List */}
          {filteredEvents.length > 0 && (
            <Stack spacing={2} role="list" aria-label="Events list">
              {filteredEvents.map((event) => (
                <Card
                  key={event.id}
                  role="listitem"
                  aria-label={`Event: ${event.name}`}
                  sx={{
                    "&:hover": {
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      transform: "translateY(-2px)",
                    },
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => handleEventClick(event.id)}
                >
                  <CardContent>
                    <Typography variant="h6" component="h3" fontWeight="bold" gutterBottom>
                      {event.name}
                    </Typography>

                    {event.nextMeetingTime ? (
                      <Stack spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <TimeIcon sx={{ color: "text.secondary", fontSize: "1.2rem" }} />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {formatDateTime(event.nextMeetingTime.beginMeetingTime).date}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatDateTime(event.nextMeetingTime.beginMeetingTime).time} -{" "}
                              {formatDateTime(event.nextMeetingTime.endMeetingTime).time}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PeopleIcon sx={{ color: "text.secondary", fontSize: "1.2rem" }} />
                          <Typography variant="body2" color="text.secondary">
                            {tCapacity}{" "}
                            {event.nextMeetingTime.capacity === 0
                              ? tCapacityUnlimited
                              : event.nextMeetingTime.capacity === 1
                                ? tCapacityPerson
                                : tCapacityPeople(event.nextMeetingTime.capacity)}
                          </Typography>
                        </Stack>

                        <Typography variant="caption" color="text.secondary">
                          {tDuration}{" "}
                          {formatDuration(event.nextMeetingTime.beginMeetingTime, event.nextMeetingTime.endMeetingTime)}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {tNoUpcoming}
                      </Typography>
                    )}

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEventClick(event.id);
                        }}
                      >
                        {tViewDetails}
                      </Button>

                      {canManage && (
                        <Box onClick={(e) => e.stopPropagation()}>
                          <AddEventTimesModal
                            onSubmit={(meetingTimes, eventId) => handleCreateMeetingTimes(eventId, meetingTimes)}
                            disabled={creatingMeetingTimes}
                            eventId={event.id}
                          />
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
